<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WizardField } from '@/types/wizard-forms'

const props = defineProps<{
  fields: WizardField[]
}>()

const emit = defineEmits<{
  select: [fieldId: number]
  move: [fieldId: number, gridRow: number, gridColumn: number]
  insertBefore: [fieldId: number, targetRow: number]
}>()

const { t } = useI18n()

const selectedFieldId = ref<number | null>(null)

const maxRow = computed(() => {
  const rows = props.fields.map((f) => f.grid_row)
  return Math.max(1, ...rows) + 1
})

const maxCol = computed(() => {
  const cols = props.fields.map((f) => f.grid_column)
  return Math.max(1, ...cols) + 1
})

const rows = computed(() =>
  Array.from({ length: maxRow.value }, (_, i) => i + 1),
)

const cols = computed(() =>
  Array.from({ length: maxCol.value }, (_, i) => i + 1),
)

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${maxCol.value}, minmax(140px, 1fr))`,
}))

function fieldAt(row: number, col: number): WizardField | undefined {
  return props.fields.find((f) => f.grid_row === row && f.grid_column === col)
}

function fieldsAt(row: number, col: number): WizardField[] {
  return props.fields.filter((f) => f.grid_row === row && f.grid_column === col)
}

function hasDuplicate(row: number, col: number): boolean {
  return fieldsAt(row, col).length > 1
}

function selectField(fieldId: number): void {
  selectedFieldId.value = fieldId
  emit('select', fieldId)
}

const dragFieldId = ref<number | null>(null)
const insertHoverRow = ref<number | null>(null)
const dragOverCell = ref<{ row: number; col: number } | null>(null)

function onFieldDragStart(fieldId: number): void {
  dragFieldId.value = fieldId
}

function onDragEnd(): void {
  dragFieldId.value = null
  insertHoverRow.value = null
  dragOverCell.value = null
}

// Cell drop handling
function onCellDragOver(event: DragEvent, row: number, col: number): void {
  event.preventDefault()
  insertHoverRow.value = null
  dragOverCell.value = { row, col }
}

function onCellDragLeave(event: DragEvent): void {
  const cell = event.currentTarget as HTMLElement
  // Ignore leave events that are just moving into a child element (e.g. the field button)
  if (cell.contains(event.relatedTarget as Node | null)) return
  dragOverCell.value = null
}

function onCellDrop(row: number, col: number): void {
  if (dragFieldId.value === null) return
  dragOverCell.value = null
  emit('move', dragFieldId.value, row, col)
  dragFieldId.value = null
  insertHoverRow.value = null
}

// Insert-before zone handling
function onInsertZoneDragOver(event: DragEvent, targetRow: number): void {
  event.preventDefault()
  event.stopPropagation()
  insertHoverRow.value = targetRow
  dragOverCell.value = null
}

function onInsertZoneDragLeave(event: DragEvent): void {
  const zone = event.currentTarget as HTMLElement
  // Ignore leave events caused by entering a child element (the label span)
  if (zone.contains(event.relatedTarget as Node | null)) return
  insertHoverRow.value = null
}

function onInsertZoneDrop(targetRow: number): void {
  if (dragFieldId.value === null) return
  emit('insertBefore', dragFieldId.value, targetRow)
  dragFieldId.value = null
  insertHoverRow.value = null
  dragOverCell.value = null
}

function fieldTypeLabel(type: string): string {
  return t(`wizard_forms.field_types.${type}`)
}
</script>

<template>
  <div class="overflow-x-auto">
    <div class="flex flex-col min-w-max">
      <template v-for="row in rows" :key="row">
        <!--
          Insert-before zone: always rendered at fixed height h-4 so no layout shift
          occurs at drag start or during hover. Only non-layout CSS properties change
          (border-color, background, opacity) — layout-changing props like height are
          never modified after the initial render.
        -->
        <div
          class="h-4 rounded border-2 border-dashed flex items-center justify-center transition-colors duration-100"
          :class="dragFieldId !== null
            ? insertHoverRow === row
              ? 'border-emerald-500 bg-emerald-500/10 cursor-row-resize'
              : 'border-slate-700 cursor-row-resize'
            : 'border-transparent pointer-events-none'"
          @dragover="onInsertZoneDragOver($event, row)"
          @dragleave="onInsertZoneDragLeave"
          @drop.stop="onInsertZoneDrop(row)"
        >
          <span
            class="text-xs font-medium pointer-events-none select-none transition-opacity duration-100"
            :class="insertHoverRow === row ? 'text-emerald-400 opacity-100' : 'opacity-0'"
          >
            {{ t('wizard_forms.grid.insert_here') }}
          </span>
        </div>

        <!-- Row of cells — mb-2 provides visual spacing; insert zone above provides the drop surface -->
        <div class="grid gap-2 mb-2" :style="gridStyle">
          <div
            v-for="col in cols"
            :key="col"
            class="relative min-h-16 rounded-lg border-2 transition-colors"
            :class="[
              dragOverCell?.row === row && dragOverCell?.col === col
                ? 'border-emerald-500/60 bg-emerald-500/5'
                : hasDuplicate(row, col)
                  ? 'border-yellow-500/60 bg-yellow-500/5'
                  : fieldAt(row, col)
                    ? 'border-slate-600 bg-slate-700/40'
                    : 'border-dashed border-slate-700 bg-slate-800/30',
            ]"
            @dragover="onCellDragOver($event, row, col)"
            @dragleave="onCellDragLeave"
            @drop="onCellDrop(row, col)"
          >
            <!-- Duplicate warning badge -->
            <div
              v-if="hasDuplicate(row, col)"
              class="absolute top-1 right-1 z-10 inline-flex items-center gap-1 rounded bg-yellow-500/20 border border-yellow-500/40 px-1.5 py-0.5"
              :title="t('wizard_forms.grid.duplicate_warning')"
            >
              <svg class="w-3 h-3 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span class="text-xs text-yellow-400 font-medium">{{ fieldsAt(row, col).length }}</span>
            </div>

            <!-- Cell coordinates label -->
            <div class="absolute top-1 left-1 text-xs text-slate-600 font-mono leading-none">
              {{ row }},{{ col }}
            </div>

            <!-- Field cards -->
            <div class="pt-5 px-2 pb-2 space-y-1">
              <button
                v-for="field in fieldsAt(row, col)"
                :key="field.id"
                type="button"
                draggable="true"
                class="w-full text-left rounded-md px-2.5 py-2 text-xs transition-colors"
                :class="[
                  selectedFieldId === field.id
                    ? 'bg-emerald-500/25 border border-emerald-500/50 text-emerald-300'
                    : 'bg-slate-700 border border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white',
                ]"
                @click="selectField(field.id)"
                @dragstart="onFieldDragStart(field.id)"
                @dragend="onDragEnd"
              >
                <span class="block font-medium truncate">{{ field.label }}</span>
                <span class="text-slate-400 mt-0.5 block">{{ fieldTypeLabel(field.type) }}</span>
                <span
                  v-if="field.is_required"
                  class="inline-block mt-0.5 text-red-400 text-xs"
                >*</span>
              </button>
            </div>

            <!-- Empty cell hint -->
            <div
              v-if="fieldsAt(row, col).length === 0"
              class="absolute inset-0 flex items-center justify-center"
            >
              <span class="text-xs text-slate-700">{{ t('wizard_forms.grid.empty_cell') }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <p v-if="props.fields.length === 0" class="mt-4 text-sm text-slate-500 text-center">
      {{ t('wizard_forms.grid.no_fields') }}
    </p>
  </div>
</template>
